import React, { useRef, useMemo, useCallback, useState } from 'react';
import { throttle } from 'lodash-es';
import { useDispatch, useSelector, history, useLocation, useIntl } from 'umi';
import { Bubble, Popover, Collapse } from 'tea-component';
import { formatGamePhase, parseUrlInfo } from '@/pages/dashboard/utils/utils';
import defaultLogo from '@/pages/dashboard/assets/img/svg/gameError.svg';
import DefaultIcon from '@/pages/dashboard/assets/svg/console-game-default.svg';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { addFollowGame, removeFollowGame } from '@/pages/dashboard/services/dashboard';
import { GameRowProps, FollowProps, GameInfoHeaderProps, GameInfoType } from '../types';
import { DB_DASHBOARD_LOCAL_FILTER_SAVE_SCHEME_KEY, DEFAULT_WIDTH } from '@/pages/dashboard/_config/constant';
import { ACCESS_LINK, IS_GLOBAL } from '@/pages/home/_config/config';
import './GameRow.scss';
import ACard from '../../../ACard';
// import { getRequestPermissionUrl } from '@home/utils/urlConfig';

// 订阅按钮
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Follow = (props: FollowProps) => {
  const { gameInfo, gameList, followList, recentList } = props;
  const { game_code: gameCode, isFollow = false } = gameInfo ?? {};
  // 收藏接口所需的所有变量存入引用，方便按钮做防抖处理
  const saveParams = useRef({
    isFollow,
    gameCode,
    followList,
    recentList,
    gameList,
  });
  Object.assign(saveParams.current ?? {}, {
    isFollow,
    gameCode,
    followList,
    gameList,
  });

  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const setAllGameFollowing = (gameCode: string, isFollow: boolean) => {
    // 此处不会请求 followList 列表，只为将状态保存在前端，切换到 我的游戏 会自动请求 followList
    const { followList, gameList } = saveParams.current ?? {};

    // 此处更新 followList 和 gameList
    let targetGame: any = null;
    let nextFollowList = [...(followList ?? [])];
    gameList?.forEach((item: any) => {
      if (gameCode === item.game_code) {
        const flag = gameCode === item.game_code ? isFollow : item.isFollow;
        targetGame = {
          ...item,
          isFollow: flag,
        };
      }
    });

    // 若当前为收藏则往 followList 新增目标否则过滤掉取消收藏的
    if (isFollow && targetGame) {
      nextFollowList.push(targetGame);
    } else {
      nextFollowList = nextFollowList
        ?.map((followGame) => {
          if (followGame.game_code === gameCode) {
            const flag = gameCode === followGame.game_code ? isFollow : followGame.isFollow;

            return {
              ...followGame,
              isFollow: flag,
            };
          }
          return followGame;
        })
        ?.filter((game) => game.isFollow);
    }
    dispatch({
      type: 'dashboard/save',
      payload: {
        followList: nextFollowList,
      },
    });
  };

  // 节流处理防止短时间多次点击，目前延迟为 1s
  const handleClick = useCallback(
    throttle(
      () => {
        const { isFollow, gameCode } = saveParams.current ?? {};
        if (isFollow) {
          removeFollowGame({
            game_code: gameCode,
          }).then(() => {
            setAllGameFollowing(gameCode, false);
          });
        } else {
          addFollowGame({
            game_code: gameCode,
          }).then(() => {
            setAllGameFollowing(gameCode, true);
          }).catch(e => {
            console.log(e);
          });
        }
      },
      500,
      {
        leading: true,
        trailing: false,
      },
    ),
    [],
  );

  return (
    <Bubble
      dark
      placement="top"
      trigger="hover"
      content={
        isFollow
          ? formatMessage({ id: 'game_selector_following_bubble' })
          : formatMessage({ id: 'game_selector_follow_bubble' })
      }
    >
      <div
        className="drawer-context-left-icons"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleClick();
        }}
      >
        <SvgIcon type={isFollow ? 'star-action' : 'star-m'} className="drawer-context-left-icon" />
      </div>
    </Bubble>
  );
};

// 游戏信息
const GameInfo = (props: GameInfoHeaderProps) => {
  const {
    gameInfo, // 游戏信息
    isMajorVersion = false, // 是否主版本游戏列表
    showAll = false, // All主版本标签在多个子版本存在时出现
    searchValue = '', // 搜索值
    // gameList = [],
    // followList = [],
    // recentList = [],
    isShowRightIcon,
    type: listType,
  } = props;
  const { logo, name = '', desc = '', game_type, game_phase: gamePhase, is_access: gameAccess, game_phase_list: gamePhaseList } = gameInfo ?? {};
  // 和后端确认后，直接拿第一个的phaseType就行
  const phaseType = gamePhaseList?.[0]?.phase_type || '';

  const isNewGame = gameInfo?.is_new_game || gameInfo?.game_List?.some((game: any) => game?.is_new_game);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const { formatMessage } = useIntl();
  const [nameContent, setNameContent] = useState(null);
  const [descContent, setDescContent] = useState(null);
  const logoMap: {
    [key: string]: { className: string; defaultLogo: any; };
  } =
  {
    mobile: {
      className: 'game-info-header-logo-mobile',
      defaultLogo: DefaultIcon,
    },
    pc: {
      className: 'game-info-header-logo-pc',
      defaultLogo: DefaultIcon,
    },
    console: {
      className: 'game-info-header-logo-console',
      defaultLogo: DefaultIcon,
    },
  };
  const gameType: string[] = ['mobile', 'pc', 'console'];
  const type: any = gameType?.includes(game_type?.split(',')[0]) ? game_type?.split(',')[0] : 'mobile';
  const gameLogoConfig = logoMap[type];

  const replaceMarkText = (text: string) => {
    let searchReg = '';
    // 组装正则表达式：表示每个字符后面可能存在空格
    for (const char of searchValue) {
      searchReg += `${char}\\s*`;
    }
    const regex = new RegExp(searchReg, 'gi');
    return text?.replace(regex, (matchText) => `<span style='color: #326FF0'>${matchText}</span>`);
  };
  const renderContent = (text: any, ref: any, isDesc = false): void => {
    const contentText = ref?.current?.scrollHeight > ref?.current?.clientHeight ? text : null;
    isDesc ? setDescContent(contentText) : setNameContent(contentText);
    return contentText;
  };
  const gameTypeIcon = useMemo(() => {
    if (gameInfo?.game_type?.includes('mobile') && !gameInfo.game_type?.includes('pc')) {
      return 'other-icon';
    }
    if (gameInfo?.game_type?.includes('pc')) {
      return 'monitor';
    }
    if (gameInfo?.game_type === 'console') {
      return 'merit';
    }
  }, [gameInfo]);

  return (
    <div className="drawer-context">
      <div className="drawer-context-left">
        <div className="drawer-context-left-cover">
          <div className={`game-info-header-logo ${gameLogoConfig?.className}`}>
            <img src={logo || gameLogoConfig?.defaultLogo || defaultLogo} />
          </div>
          {/* 优先展示 new，再过滤掉非 gl\cbt\sl 的阶段 */}
          <div className={`${formatGamePhase(gamePhase, 'badgeClassName', isNewGame, phaseType)}`}>{formatGamePhase(gamePhase, 'type', isNewGame, phaseType)}</div>
        </div>
        <div className="drawer-context-flex">
          <div className="drawer-context-left-text-info">
            <Bubble placement="top" trigger="hover" dark content={nameContent}>
              <div
                ref={titleRef}
                className="drawer-context-left-text"
                onMouseEnter={() => renderContent(name, titleRef)}
                dangerouslySetInnerHTML={{
                  __html: isMajorVersion && searchValue ? replaceMarkText(name) : name,
                }}
              />
            </Bubble>
            {/* {isMajorVersion && <Follow gameInfo={gameInfo} gameList={gameList} followList={followList} recentList={recentList} />} */}
            {showAll && <span className="drawer-context-left-tag">{'All'}</span>}
          </div>
          <Bubble placement="top" trigger="hover" dark content={descContent}>
            <div
              ref={descRef}
              className="drawer-context-left-desc"
              onMouseEnter={() => renderContent(desc, descRef, true)}
            >
              {/* 默认给pc，none不展图标 */}
              {
                gameInfo?.game_type !== 'none' && <SvgIcon className="drawer-context-left-desc-icon" type={gameTypeIcon || 'monitor'} />
              }
              {desc}
            </div>
          </Bubble>
        </div>
      </div>
      <Bubble placement="top" trigger="hover" dark content={IS_GLOBAL ? formatMessage({ id: 'game_selector_game_no_access' }) : ''}>
        <span className={`${!gameAccess && listType !== 'following' ? IS_GLOBAL ? 'dbd-drawer-more-opt-global' : 'dbd-drawer-more-opt' : 'dbd-drawer-more-text'}`}>
          {(!gameAccess && listType !== 'following') && formatMessage({ id: 'game_selector_game_get_access' })}
          {
            isShowRightIcon && <SvgIcon type="point-right" className="dbd-drawer-icon-pointRight" />
          }
        </span>
      </Bubble>
    </div>
  );
};

const GameRow = (props: GameRowProps) => {
  const { gameInfo, searchValue, setGameModalVisible, type, menuWidth } = props;
  const [selectedGame, setSelectedGame] = useState<GameInfoType | null>(null);
  const [visible, setVisible] = useState(false);
  const { formatMessage } = useIntl();
  const {
    cover: hostGameLogo,
    game_name: hostGameNameEN,
    game_cn_name: hostGameNameCN,
    game_code: hostGameCode,
    game_List,
  } = gameInfo ?? {};

  const {
    gameList: allGameList,
    followList,
    recentList,
  } = useSelector((state: any) => {
    const { gameList, currentGame, followList, recentList, leftMenuWidth } = state.dashboard;
    return {
      gameList,
      currentGame,
      followList,
      recentList,
      leftMenuWidth
    };
  });
  const location = useLocation();

  const handleRequestAccess = () => {
    if (IS_GLOBAL) {
      return;
    }
    const url = `${ACCESS_LINK}&role_name=game_level_3&module=${hostGameCode}`;
    // const url = getRequestPermissionUrl();
    window.open(url);
  };

  // const handleGameClick = (e) => {
  //   const svgNodeArr = ['svg', 'g', 'text', 'path','use']
  //   if(!svgNodeArr?.includes(e?.target?.tagName) ){
  //     const mainGameIsAccess = gameInfo.is_access;
  //     const childGameIsAccessGame = gameInfo?.game_List?.filter((items) => items.is_access) || [];
  //     const isHaveAccess = mainGameIsAccess || childGameIsAccessGame?.length > 0;
  //     if (!isHaveAccess) {
  //       handleRequestAccess();
  //       return;
  //     }
  //
  //   const gameCode = mainGameIsAccess ? gameInfo.game_code : childGameIsAccessGame?.[0]?.game_code;
  //   const { system, realPath } = parseUrlInfo(history.location.pathname);
  //   history.push(`/${system}/game/${gameCode}/${realPath}`);
  //   // 切换游戏清除筛选器继承
  //   localStorage.removeItem('lockCacheValue');
  //   setGameModalVisible?.(false);
  //   }
  // };

  const getUrl = (item: any, isMajorVersion = false) => {
    // 主版本游戏is_access用于区分有权限游戏和无权限游戏
    // 需求添加主版本游戏可点击跳转逻辑（不能用is_access判断该游戏权限）
    if (isMajorVersion && game_List?.length > 0) {
      const game = game_List?.find(v => v?.game_code === item?.game_code);
      if (!game?.is_access && !IS_GLOBAL) {
        return `${ACCESS_LINK}&role_name=game_level_3&module=${hostGameCode}`;
      }
    }
    if (!item?.is_access && !IS_GLOBAL) {
      return `${ACCESS_LINK}&role_name=game_level_3&module=${hostGameCode}`;
    }

    const gameCode = item?.game_code;
    const { system, realPath } = parseUrlInfo(location.pathname);

    return `/${system}/game/${gameCode}/${realPath}`;
  };

  const handleGameClickItem = (item: { is_access: any; game_code: any; game_List: any; }, isMajorVersion = false) => {
    // 主版本游戏is_access用于区分有权限游戏和无权限游戏
    // 需求添加主版本游戏可点击跳转逻辑（不能用is_access判断该游戏权限）
    if (isMajorVersion && game_List?.length > 0) {
      const game = game_List?.find(v => v?.game_code === item?.game_code);
      if (!game?.is_access) {
        handleRequestAccess();
        return;
      }
    }
    if (!item?.is_access) {
      handleRequestAccess();
      return;
    }
    const gameCode = item?.game_code;
    const { system, realPath } = parseUrlInfo(location.pathname);

    history.push(`/${system}/game/${gameCode}/${realPath}`);
    // 切换游戏清除筛选器继承
    localStorage.removeItem('lockCacheValue');
    // 切换游戏删除筛选器方案的继承
    localStorage.removeItem(DB_DASHBOARD_LOCAL_FILTER_SAVE_SCHEME_KEY);
    setGameModalVisible?.(false);
  };

  const [childGameList, accessGameList, noAccessGameList] = useMemo(() => {
    // 子版本列表：过滤子版本只有一个和主版本是相同game_code的情况
    const childGameList = game_List?.length === 1 ? game_List?.filter((item: { game_code: string; }) => item?.game_code !== gameInfo?.game_code) : game_List;
    return [childGameList, childGameList?.filter((item: { is_access: any; }) => item.is_access), childGameList?.filter((item: { is_access: any; }) => !item.is_access)];
  }, [gameInfo]);
  // const isExpandNoAcess = useMemo(() => {
  //   return accessGameList?.length === 0 && noAccessGameList?.length>0;
  // }, [childGameList])

  // 动态计算弹窗位置，避免有滚动条时, 弹窗位置偏移
  const popoverOverlayStyle: Record<string, string> = {
    left: `${470 + (menuWidth || DEFAULT_WIDTH)}px`,
  };

  return (
    <>
      <Popover
        visible={visible}
        onVisibleChange={(visible) => setVisible(visible)}
        placement="right-start"
        trigger="hover"
        updateOnDimensionChange={true}
        overlayStyle={popoverOverlayStyle}
        overlay={
          (childGameList?.length > 0) && <div className="dbd-sub-game-wrap">
            <div className="dbd-sub-game-wrap-inner dbd-drawer-more dbd-nav-drawer">
              {accessGameList?.length > 0 && accessGameList?.map((item) => (
                <ACard key={item?.game_code} url={getUrl(item)}>
                  <div
                    className={`drawer-item ${(item?.game_code === selectedGame?.game_code) && visible ? 'is-selected' : ''}`}
                    onClick={() => handleGameClickItem(item)}
                  >
                    <GameInfo
                      showAll={accessGameList?.length + noAccessGameList?.length > 1 && item?.game_code === selectedGame?.game_code}
                      gameInfo={{
                        ...item,
                        logo: item?.cover,
                        name: item?.game_name || item?.game_cn_name,
                        desc: item?.game_cn_name || item?.game_name,
                      }}
                    />
                  </div>
                </ACard>
              ))}
              {/* 都没有权限，则默认展开 */}
              {/* todo 暂时默认展开，如果确认需要默认收起，则需要展开的时候重新计算位置，否则div可能超出视界（计算方法：根据updateOnDimensionChange属性， 小于5个，触发window resize即可重新计算） */}
              <Collapse
                // defaultActiveIds={accessGameList?.length < 1 ? ['1'] : []}
                defaultActiveIds={[]}
                icon={(active) => <SvgIcon className="dbd-collapse-icon" type={active ? 'point-top' : 'point-down'} />}
                iconPosition="right"
                onActive={() => {
                  // 展开/收起时触发一次重算，避免浮层被视口裁切
                  setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
                }}
              >
                {
                  (noAccessGameList?.length > 0 && !IS_GLOBAL) && <Collapse.Panel id="1" title={formatMessage({ id: 'game_selector_game_no_autority' })}>
                    {noAccessGameList?.map((item) => (
                      <ACard key={item?.game_code} url={IS_GLOBAL ? '' : `${ACCESS_LINK}&role_name=game_level_3&module=${hostGameCode}`} isStopPropagation={false}>
                        <div
                          className={`drawer-item ${(item?.game_code === selectedGame?.game_code) && visible ? 'is-selected' : ''}`}
                          onClick={handleRequestAccess}
                        >
                          <GameInfo
                            showAll={accessGameList?.length + noAccessGameList?.length > 1 && item?.game_code === selectedGame?.game_code}
                            gameInfo={{
                              ...item,
                              logo: item?.cover,
                              name: item?.game_name || item?.game_cn_name,
                              desc: item?.game_cn_name || item?.game_name,
                            }}
                          />
                        </div>
                      </ACard>
                    ))}
                  </Collapse.Panel>
                }
              </Collapse>
            </div>
          </div>
        }
      >
        <div>
          <ACard url={getUrl(gameInfo, true)}>
            <div
              className={`drawer-item ${(gameInfo?.game_code === selectedGame?.game_code) && visible ? 'is-popover' : ''}`}
              onMouseEnter={() => setSelectedGame(gameInfo)}
              onClick={() => {
                // 游戏列表游戏可点击跳转
                handleGameClickItem(gameInfo, true);
              }}
            >
              <GameInfo
                gameInfo={{
                  ...gameInfo,
                  logo: hostGameLogo,
                  name: hostGameNameEN || hostGameNameCN,
                  desc: hostGameNameCN || hostGameNameEN,
                }}
                isMajorVersion={true}
                searchValue={searchValue}
                gameList={allGameList}
                followList={followList}
                recentList={recentList}
                isShowRightIcon={!!childGameList?.length}
                type={type}
              />
            </div>
          </ACard>
        </div>
      </Popover>
    </>
  );
};

export default GameRow;
